import React, { useEffect, useState, useContext } from "react";
import CollabrativeImg from "../../../../Assets/Images/CollabrativeImg.png";
import VisionaryImg from "../../../../Assets/Images/vis.png";
import StrategicImg from "../../../../Assets/Images/strat.png";
import PerformanceImg from "../../../../Assets/Images/perfor.png";
import HeaderPreview from "../../../GeneratePdf/HeaderPreview";
import FooterPreview from "../../../GeneratePdf/FooterPreview";
import GroupReportContext from "../../../../Context/GroupReportContext";
import { useGroupReportDate } from "../../../../Context/GroupReportDate";
import { applyFullTieOrder } from "../../../../utils/quadrantTieOrder";

// Hierarchy for tie-breaking: lower number = higher priority
const LEADERSHIP_HIERARCHY = {
  collaborative: 0,  // Highest priority
  visionary: 1,
  strategic: 2,
  performance: 3    // Lowest priority
};

// Comparator function for sorting leadership quadrants with tie-breaking
const compareLeadershipQuadrants = (a, b) => {
  // First compare by score (descending)
  const scoreDiff = b.score - a.score;
  if (scoreDiff !== 0) return scoreDiff;

  // If scores are equal, compare by hierarchy (ascending - lower number = higher priority)
  const aHierarchy = LEADERSHIP_HIERARCHY[a.name.toLowerCase()] ?? 999;
  const bHierarchy = LEADERSHIP_HIERARCHY[b.name.toLowerCase()] ?? 999;
  return aHierarchy - bHierarchy;
};

const LABELS = ["strategic", "visionary", "performance", "collaborative"];

// New array with correct order: Performance, Collaborative, Visionary, Strategic
const ORDERED_LABELS = ["performance", "collaborative", "visionary", "strategic"];

const leadershipData = {
  collaborative: {
    title: "COLLABORATIVE LEADERSHIP GROUPS",
    image: CollabrativeImg,
    intro: [
      `Collaborative Leadership is an extension of the Heart quadrant. These types of groups excel in creating environments where everyone feels important and valued. They lead in ways that seek to foster a cooperative atmosphere and emphasize respect among team members, helping everyone perform their best. `,
      `Often referred to as "the People’s Leader,” these groups focus first on relationships and may be naturally attuned to understand and constructively manage the feelings, wants, and needs happening beneath the surface.`
    ],
    details: [
`This makes them empathetic and considerate but they need to be careful not to get too caught up in people's issues to the point where it derails objectives and overrides healthy boundaries. At the heart of collaborative leadership is the belief that working together brings better results than working alone, "If you want to go fast, go alone. If you want to go far, go together."`
    ],
    contributions: [
      "Collaborative Leadership Groups empower team members by delegating authority and encouraging initiative, which boosts confidence and accountability.",
      "They excel at bridging interdepartmental gaps, aligning diverse interests and goals to enhance cooperation and reduce silos. Their approach to stakeholder engagement builds trust and fosters strong relationships crucial for success.",
      "These groups cultivate a positive culture that values teamwork, respect, and diversity, making the workplace more welcoming and productive. Additionally, their leadership style enhances morale and job satisfaction, making employees feel valued and reducing turnover.",
    ],
    challenges: [
      "Despite their strengths, Collaborative Leadership Groups face several challenges. Emphasizing consensus can lead to groupthink, where the desire for harmony results in irrational or dysfunctional decisions. Their consensus-driven approach may also slow down decision-making, especially in critical situations.",
      "The focus on relationships and feelings can risk losing objectivity and becoming overly involved in personal issues. Addressing underperformance can be challenging as they prioritize maintaining positive relationships.",
      "Finally, collaborative approaches often require more time and resources, posing challenges in resource management without compromising other areas.",
    ],
  },
  visionary: {
    title: "VISIONARY LEADERSHIP GROUPS",
    image: VisionaryImg,
    intro: [
      `Visionary Leadership is an extension of the Soul quadrant. These groups excel in bringing leaders, teams, or the organization back to the deeper meaning and purpose behind all the daily activity. They have a unique knack for seeing what others don't and articulating a future that motivates everyone to rally around a shared vision. Visionary groups often utilize symbols, ceremonies, and storytelling to enrich their leadership narrative, creating a resonant culture that transcends conventional business metrics and fosters a strong sense of community and shared purpose.`],
    details: [
      `However, they often face challenges in translating broad visions into actionable steps, which can frustrate teams eager to implement these ideas. Balancing the inspirational aspects of leadership with the necessity for concrete, actionable plans remains a key challenge for these groups. These groups embody wisdom and perspective, often motivated towards building and leaving a legacy.`
    ],
    contributions: [
      "Visionary Leadership Groups introduce groundbreaking ideas that redefine possibilities, leading to significant breakthroughs and organizational future-proofing.",
      "They motivate teams by linking daily tasks to a broader mission, significantly boosting engagement and commitment. Their innovative and forward-thinking initiatives enhance brand reputation, attracting customers, partners, and investors.",
      "Visionary groups also transform organizational culture, fostering a spirit of innovation and continuous improvement at all levels. By mentoring and empowering the next generation, they cultivate forward-thinking leaders who can sustain the vision and drive future success.",
    ],
    challenges: [
      "Visionary Leadership Groups may struggle to translate grand visions into actionable steps, causing team frustration when roadmaps are unclear. Keeping team enthusiasm high over time, especially through challenges and slow phases, can be difficult.",
      "They often face skepticism or resistance from those hesitant to change, requiring persuasive skills and substantial evidence to support their visions. Securing enduring commitment to their vision, particularly from stakeholders favoring short-term gains, demands clear communication of long-term benefits.",
      "Additionally, their advanced or unconventional ideas may lead to feelings of isolation or misunderstanding, impacting their effectiveness and job satisfaction.",
    ],
  },
  strategic: {
    title: "STRATEGIC LEADERSHIP GROUPS",
    image: StrategicImg,
    intro: [
      `Strategic Leadership is an extension of the Mind quadrant. They play a crucial role in shaping an organization by developing robust structures, policies, and frameworks that lay the foundation for success. `,
      'These groups excel at connecting big-picture goals with practical execution. Strategic Leadership Groups are adept at defining clear roles and responsibilities within their teams, ensuring that every member knows their part in achieving organizational success.'],
    details: [
      `They are skilled at leveraging existing frameworks and processes to maximize results, always with an eye toward continual improvement and adaptation. However, their focus on control and structure can sometimes lead to challenges, such as overly autocratic decision-making or inflexible policies.`,
      `These are the individuals who combine analytical prowess with operational efficiency, ensuring that the organization not only sets ambitious goals but also achieves them through meticulous strategy and execution. They are the masterminds behind the scenes, turning visions into reality with precision and foresight. `,
    ],
    contributions: [
      "Strategic Leadership Groups provide a clear direction, ensuring all team members understand their roles in achieving organizational goals. They excel in creating detailed, actionable plans that simplify complex goals, enhancing efficiency and effectiveness.",
      "These groups proactively identify and mitigate potential risks, protecting the organization against financial, reputational, and operational setbacks. They promote continuous professional growth and knowledge sharing, helping team members advance their skills.",
      "By utilizing advanced analytics and big data, they significantly improve decision-making processes, providing a competitive edge.",
    ],
    challenges: [
      "Strategic Leadership Groups may sometimes overemphasize control, potentially stifling creativity and autonomy among team members, which can dampen motivation and innovation.",
      "They might resist feedback that challenges their vision or strategies, missing critical insights that could enhance outcomes. Their focus on strategic goals may lead them to overlook the importance of employee morale and workplace culture.",
      "Adapting small-scale strategies to broader organizational contexts can present significant challenges. Additionally, heavy reliance on strategic leadership can risk organizational stagnation and create decision-making bottlenecks.",
    ],
  },
  performance: {
    title: "PERFORMANCE LEADERSHIP GROUPS",
    image: PerformanceImg,
    intro: [
      `Performance Leadership is an extension of the Strength quadrant. Performance Leadership Groups are pivotal in driving organizational success through enhanced efficiency and problem-solving capabilities. `,
      `They excel at translating high-level ideas into concrete actions. These groups establish clear benchmarks and objectives that motivate team members to exceed expectations while balancing authority and resources to deliver top-notch customer service.`],
    details: [
      `However, their focus on goals and performance might lead them to view individuals more as means to an end rather than as whole persons, occasionally overlooking the personal contributions and individual needs of their team members.`,
      `Their knack for turning strategic visions into actionable steps ensures that the organization operates smoothly and achieves notable results.`,
    ],
    contributions: [
      "Performance Leadership Groups are highly effective at setting and reaching ambitious targets, often exceeding standard expectations through their focus on results.",
      "They are proactive in identifying problems and quick to develop practical solutions to overcome them. By leading by example, they demonstrate a strong work ethic and commitment to the organization's goals, inspiring others to follow suit.",
      "These groups set clear goals and benchmarks, motivating employees to strive for excellence and fostering a high-performance culture. They also manage resources effectively, ensuring that the organization's assets are utilized in the most efficient manner possible.",
    ],
    challenges: [
      "Performance Leadership Groups may focus too heavily on outcomes at the expense of process quality and employee well-being, potentially leading to burnout and dissatisfaction.",
      "Their task-oriented nature can result in a management style that feels impersonal and transactional to some employees. They might prioritize short-term gains over long-term sustainability, potentially compromising the future viability of certain initiatives.",
      "The high-pressure environment they cultivate can negatively impact the work-life balance of their teams, affecting overall job satisfaction. Additionally, their high expectations and focus on results can make it difficult to appropriately handle and learn from failures.",
    ],
  },
};

const LeadershipStyleCollabrativeGroup = ({ apiData, previewData, reportType, pageNumber, onDataCalculated, }) => {
  const groupReportCtx = useContext(GroupReportContext);
  const { averagePercentagesLeadership } = groupReportCtx;

  const [leadershipSnapshotResults, setLeadershipSnapshotResults] = useState([]);
  const [leadershipLabels, setLeadershipLabels] = useState(LABELS);
  const [mostSelecTedStarter, setMostSelecTedStarter] = useState("");
  const [highestGroup, setHighestGroup] = useState("collaborative");
  const [calculatedPercentages, setCalculatedPercentages] = useState({});

  useEffect(() => {
    if (!previewData || !previewData.length || !apiData?.teamname) return;

    const currentTeamData = previewData.find(
      (team) => team.teamname === apiData.teamname
    );
    if (!currentTeamData) return;

    const reportData = currentTeamData.results;
    if (!reportData) return;

    const assessmentResults = [];
    const assessmentsData = Object.values(reportData);

    assessmentsData.forEach((assessment) => {
      if (!assessment?.variables) return;

      const variables = {};
      assessment.variables.forEach((variable) => {
        variables[variable.variable] = variable.score;
      });

      // Get scores
      const performance_r1_m = variables["per_round1_m"] || 0;
      const strategic_r1_m = variables["strat_round1_m"] || 0;
      const visionary_r1_m = variables["vis_round1_m"] || 0;
      const collaborative_r1_m = variables["col_round1_m"] || 0;

      const performance_r2_m = variables["per_round2_m"] || 0;
      const strategic_r2_m = variables["strat_round2_m"] || 0;
      const visionary_r2_m = variables["vis_round2_m"] || 0;
      const collaborative_r2_m = variables["col_round2_m"] || 0;

      const performance_r1_l = variables["per_round1_l"] || 0;
      const strategic_r1_l = variables["strat_round1_l"] || 0;
      const visionary_r1_l = variables["vis_round1_l"] || 0;
      const collaborative_r1_l = variables["col_round1_l"] || 0;

      const performance_r2_l = variables["per_round2_l"] || 0;
      const strategic_r2_l = variables["strat_round2_l"] || 0;
      const visionary_r2_l = variables["vis_round2_l"] || 0;
      const collaborative_r2_l = variables["col_round2_l"] || 0;

      const most_scores = [performance_r1_m, strategic_r1_m, visionary_r1_m, collaborative_r1_m,];
      const second_most_scores = [performance_r2_m, strategic_r2_m, visionary_r2_m, collaborative_r2_m,].map((s) => s / 2);
      const least_scores = [performance_r1_l, strategic_r1_l, visionary_r1_l, collaborative_r1_l,];
      const second_least_scores = [performance_r2_l, strategic_r2_l, visionary_r2_l, collaborative_r2_l,].map((s) => s / 2);

      const most_score = Math.max(...most_scores);
      const second_most_score = Math.max(...second_most_scores);
      let least_score = Math.max(...least_scores);
      const second_least_score = Math.max(...second_least_scores);

      if (least_score >= second_least_score) {
        least_score = second_least_score - 1;
      }

      const total =
        most_score + second_most_score + least_score + second_least_score;

      const most_per = (most_score / total) * 100;
      const second_most_per = (second_most_score / total) * 100;
      const least_per = (least_score / total) * 100;
      const second_least_per = (second_least_score / total) * 100;

      const labels_arr = ["performance", "strategic", "visionary", "collaborative"];

      const mostValues = [performance_r1_m, strategic_r1_m, visionary_r1_m, collaborative_r1_m,];
      const secondMostValues = [performance_r2_m, strategic_r2_m, visionary_r2_m, collaborative_r2_m,];
      const leastValues = [performance_r1_l, strategic_r1_l, visionary_r1_l, collaborative_r1_l,];
      const secondLeastValues = [performance_r2_l, strategic_r2_l, visionary_r2_l, collaborative_r2_l,];

      const most_score_label = Math.max(...mostValues);
      const second_most_score_label = Math.max(...secondMostValues);
      const least_score_label = Math.max(...leastValues);
      const second_least_score_label = Math.max(...secondLeastValues);

      const getLabel = (values, maxValue) => labels_arr[values.indexOf(maxValue)];

      const most_label = getLabel(mostValues, most_score_label);
      const second_most_label = getLabel(secondMostValues, second_most_score_label);
      const least_label = getLabel(leastValues, least_score_label);
      const second_least_label = getLabel(secondLeastValues, second_least_score_label);

      const getPercentageOrder = (label) => {
        if (label === most_label?.toLowerCase()) return most_per;
        if (label === second_most_label?.toLowerCase()) return second_most_per;
        if (label === least_label?.toLowerCase()) return least_per;
        if (label === second_least_label?.toLowerCase()) return second_least_per;
        return 0;
      };

      const results = [
        getPercentageOrder("strategic"),
        getPercentageOrder("visionary"),
        getPercentageOrder("performance"),
        getPercentageOrder("collaborative"),
      ];

      assessmentResults.push({
        results,
        most_label,
        scores: { most_score, second_most_score, least_score, second_least_score, },
      });
    });

    if (assessmentResults.length > 0) {
      const averageResults = assessmentResults
        .reduce((acc, curr) => {
          curr.results.forEach((score, i) => {
            if (!acc[i]) acc[i] = 0;
            acc[i] += score;
          });
          return acc;
        }, [])
        .map((total) => total / assessmentResults.length);

      const mostCommonLabel = assessmentResults.reduce((acc, curr) => {
        acc[curr.most_label] = (acc[curr.most_label] || 0) + 1;
        return acc;
      }, {});
      const overallHighest = Object.entries(mostCommonLabel).reduce(
        (max, [key, count]) => (count > max.count ? { name: key, count } : max),
        { name: "", count: -Infinity }
      ).name;

      const quadrantPercentages = {
        strategic: parseFloat(averageResults[0].toFixed(2)) || 0,
        visionary: parseFloat(averageResults[1].toFixed(2)) || 0,
        performance: parseFloat(averageResults[2].toFixed(2)) || 0,
        collaborative: parseFloat(averageResults[3].toFixed(2)) || 0,
      };

      // Use hierarchical tie-breaking for leadership quadrants
      const sorted = applyFullTieOrder(
        Object.entries(quadrantPercentages)
          .map(([name, score]) => ({ name, score }))
          .sort(compareLeadershipQuadrants),
        (q) => q.name,
        (q) => q.score
      );

      const highestQuadrant = sorted[0].name;
      const secondHighestQuadrant = sorted[1].name;

      const finalResults = { averageResults, overallHighest, quadrantPercentages, highestQuadrant, secondHighestQuadrant, };

      if (onDataCalculated) onDataCalculated(finalResults);

      setMostSelecTedStarter(overallHighest);
      setLeadershipSnapshotResults(averageResults);
      setLeadershipLabels(LABELS);
      setCalculatedPercentages(quadrantPercentages);
      setHighestGroup(highestQuadrant);
    }
  }, [previewData, apiData, onDataCalculated]);

  useEffect(() => {
    if (!averagePercentagesLeadership?.length) return;

    const getAvg = (name) =>
      Number(
        averagePercentagesLeadership
          .find((el) => el?.name?.toLowerCase() === name)
          ?.avg?.toFixed(2) ?? 0
      );

    const ordered = LABELS.map(getAvg);

    setLeadershipSnapshotResults(ordered);
    setLeadershipLabels(LABELS);

    // Use hierarchical tie-breaking for leadership quadrants
    const quadrantsWithScores = LABELS.map((name, idx) => ({
      name,
      score: ordered[idx]
    }));

    const sortedQuadrants = applyFullTieOrder(quadrantsWithScores.sort(compareLeadershipQuadrants), (q) => q.name, (q) => q.score);
    setHighestGroup(sortedQuadrants[0].name);
  }, [averagePercentagesLeadership]);



  const styles = {
    container: { padding: "16px 0px", },
    card: { marginBottom: "16px", width: "100%", },
    cardBlue: { backgroundColor: "#F1F8F1", padding: "16px", borderRadius: "5px", width: "100%", minHeight: "182px", },
    cardRed: { backgroundColor: "#FFF0EC", padding: "16px", borderRadius: "5px", marginTop: "10px", width: "100%", minHeight: "202px", },
    title: { fontSize: "18px", lineHeight: "21px", fontWeight: 700, fontFamily: "Helvetica, sans-serif", color: "#426B88", marginBottom: "13px", marginTop: 0, textTransform: "uppercase", textAlign: "left", width: "100%", },
    subtitle: { fontSize: "10px", lineHeight: "14px", fontWeight: 700, color: "#000000", fontFamily: "Helvetica, sans-serif", textTransform: "uppercase", width: "100%", margin: 0, },
    paragraph: { fontSize: "10px", paddingBottom: "16px", lineHeight: "14px", fontWeight: 400, color: "#000000", fontFamily: "Helvetica, sans-serif", width: "100%", marginTop: 0, margin: 0, },
    listItem: { fontSize: "10px", lineHeight: "14px", fontWeight: 400, color: "#000000", fontFamily: "Helvetica, sans-serif", margin: 0, fontFamily: "Helvetica, sans-serif", paddingLeft: "15px", position: "relative", maxWidth: "79ch", },
    coverImage: { width: "108px", height: "108px", marginRight: "6px", objectFit: "contain", flexShrink: 0, },
    first: { display: "flex", flexDirection: "row", alignItems: "flex-start", width: "100%", },
    text: { flex: 1, paddingBottom: "16px", fontSize: "10px", lineHeight: "14px", fontWeight: 400, color: "#000000", fontFamily: "Helvetica, sans-serif", marginTop: 0, marginBottom: 0, },
    bulletPoint: { position: "absolute", left: "6px", color: "#00577D" },
    contributionsSection: { backgroundColor: "#F1F8F1", borderRadius: "5px", padding: "10px", marginBottom: "10px", display: "flex", flexDirection: "column", gap: "1px", },
    challengesSection: { backgroundColor: "#FFF0EC", borderRadius: "5px", padding: "10px", marginBottom: 0, marginTop: 0, display: "flex", flexDirection: "column", gap: "1px", },
  };

  return (
    <div>
      <HeaderPreview />
      <div style={styles.container}>
        <h2 style={styles.title}>{leadershipData[highestGroup].title}</h2>

        <div style={styles.first}>
          <img
            src={leadershipData[highestGroup].image}
            alt={leadershipData[highestGroup].title}
            style={styles.coverImage}
          />
          <div style={{ display: 'flex', flexDirection: "column", gap: "0px" }}>
            {leadershipData[highestGroup].intro.map((text, index) => (
              <p key={index} style={styles.text}>{text}</p>
            ))}
          </div>
        </div>

        {leadershipData[highestGroup].details.map((detail, idx) => (
          <p style={styles.paragraph} key={idx}>
            {detail}
          </p>
        ))}

        <div style={styles.contributionsSection}>
          <p style={styles.subtitle}>
            POSSIBLE CONTRIBUTIONS OF {leadershipData[highestGroup].title}
          </p>
          {leadershipData[highestGroup].contributions.map((item, idx) => (
            <div style={styles.listItem} key={idx}>
              <span style={styles.bulletPoint}>•</span>
              {item}
            </div>
          ))}
        </div>

        <div style={styles.challengesSection}>
          <p style={styles.subtitle}>
            POSSIBLE CHALLENGES FOR {leadershipData[highestGroup].title}
          </p>
          {leadershipData[highestGroup].challenges.map((item, idx) => (
            <div style={styles.listItem} key={idx}>
              <span style={styles.bulletPoint}>•</span>
              {item}
            </div>
          ))}
        </div>
      </div>
      <FooterPreview pageNumber={pageNumber} reportType={reportType} />
    </div>
  );
};

export default LeadershipStyleCollabrativeGroup;
